/**
 * The authenticated app's top-level route segments — the single source of truth
 * for "which URLs must never be indexed".
 *
 * Two separate mechanisms keep the app shell out of search results, and they
 * have to agree:
 *
 *   robots.ts      → /robots.txt Disallow rules (asks crawlers not to fetch)
 *   proxy.ts       → X-Robots-Tag: noindex header (the authoritative signal,
 *                    and the one that still works when a page is linked from
 *                    somewhere external — Disallow alone does not prevent
 *                    indexing in that case)
 *
 * They had already drifted: /advances had only the Disallow, /interiors had
 * only the header, so each page carried exactly one of the two protections.
 * robots.ts now builds its list from this array, and proxy.ts asserts at
 * COMPILE TIME that its matcher covers every entry (Next.js requires the
 * matcher itself to be a static literal, so it cannot be generated).
 *
 * Adding a route group under src/app/(dashboard)? Add the segment here and tsc
 * will tell you exactly what else needs updating.
 */
export const APP_ROUTE_SEGMENTS = [
  'advances',
  'assistant',
  'attendance',
  'banking',
  'bills',
  'clients',
  'contractors',
  'departments',
  'designations',
  'diary',
  'employees',
  'estimates',
  'home',
  'interiors',
  'inventory',
  'leads',
  'ledger',
  'materials',
  'notes',
  'notifications',
  'office-expenses',
  'payments',
  'payroll',
  'profile',
  'purchase-orders',
  'reports',
  'settings',
  'sites',
  'small-works',
  'users',
  'vendors',
  'work-orders',
] as const;

export type AppRouteSegment = (typeof APP_ROUTE_SEGMENTS)[number];

/**
 * Non-dashboard routes that must also stay out of the index: the operator
 * console, the renewal wall, the one-time-token password pages, and the
 * token-addressed client surfaces (a link shared with a homeowner is not
 * something to crawl — these carry their own noindex meta as well).
 */
export const OTHER_PRIVATE_ROUTES = [
  'admin',
  'plan-expired',
  'set-password',
  'reset-password',
  'forgot-password',
  'status',
  'portal',
  'q',
] as const;

/**
 * Is this path part of the PUBLIC marketing site?
 *
 * Derived from the two lists above so a new module cannot be measured, or have
 * its visitor's first touch recorded, by accident — exactly the drift a
 * hand-kept allowlist eventually permits. Lifted out of GoogleAnalytics.tsx
 * when the attribution capture needed the same question answered; two copies of
 * this predicate would be two places for a private route to leak from.
 *
 * Everything not in those lists is the public website: home, pricing, modules,
 * the comparisons, the guides, the Tamil tree, the legal pages and /login.
 */
export function isPublicMarketingPath(pathname: string): boolean {
  const segment = (pathname || '/').split('/')[1] || '';
  return !([...APP_ROUTE_SEGMENTS, ...OTHER_PRIVATE_ROUTES] as readonly string[]).includes(segment);
}
